/**
 * Chapter 13, measuring and learning: five reads from the post to the year
 * (24), the tests (54).
 */
import { chapterSpec } from "../deck/deckModel";
import {
  Eyebrow, Foot, Headline, Slide, SourceLine, V,
  countWord, slideText,
  type TemplateContext,
} from "./kit";

/* ── 24 How we read the numbers ───────────────────────────────────────── */

/** The framework's five reads (4.5): fixed, the same for every strategy. */
const READS = [
  { key: "Each post", when: "At 24 hours and 7 days", reads: "The post’s numbers and its comments.", produces: "A hit, level or miss mark against the account’s own middle. Questions left unanswered." },
  { key: "Each series", when: "After {n} posts", reads: "The series’ middle against the account’s middle.", produces: "Grow, keep, change or stop." },
  { key: "Every month", when: "When a person makes the report", reads: "Goals against targets, the best and weakest posts, the client’s business number.", produces: "A one-page report: on track, ahead or behind per goal, and what changes next month." },
  { key: "Every quarter", when: "The scorecard, read again", reads: "Presence, Content and Community against the same competitors. The tests.", produces: "The movement per criterion. Targets kept or reset, with a reason." },
  { key: "Every year", when: "Promised against happened", reads: "What the strategy said, against what the year did.", produces: "The start of next year’s strategy." },
];

const ratio = (n: number) => (n === 0.5 ? "half" : `${n} ×`);

export function ReadingSlide({ ctx }: { ctx: TemplateContext }) {
  const measuring = ctx.doc.measuring;
  const t = slideText(ctx);
  const rules = measuring?.series_rules;
  const n = rules?.posts_before_read ?? 6;
  const quarterRead = measuring?.reads.some((r) => r.kind === "quarterly");
  return (
    <Slide>
      <div className="sv-col sv-fill">
        <Eyebrow ctx={ctx} />
        <Headline text={t.headline} fallback="Five reads, *from the post to the year.*" />
        <div className="sv-reads">
          <div className="sv-reads-track">
            {READS.map((r, i) => <span key={r.key} data-first={i === 0 || undefined} data-last={i === READS.length - 1 || undefined}><i /></span>)}
          </div>
          <div className="sv-reads-stops">
            {READS.map((r) => (
              <div key={r.key} className="sv-read">
                <p className="sv-read-key">{r.key}</p>
                <p className="sv-read-when">{r.when.replace("{n}", countWord(n))}</p>
                <div className="sv-read-part"><p className="sv-read-sub">Creo reads</p><p>{r.reads}</p></div>
                <div className="sv-read-part"><p className="sv-read-sub">It produces</p><p className="sv-read-out">{r.produces}</p></div>
              </div>
            ))}
          </div>
          {rules ? (
            <div className="sv-rules">
              <p className="sv-read-key">What happens to a series, after {countWord(n)} posts</p>
              <div className="sv-rules-row">
                <div className="sv-rule" data-tone="grow"><p><span>Grow</span><b>above <V>{ratio(rules.grow_ratio)}</V></b></p><p>the account’s middle: more posts a month.</p></div>
                <div className="sv-rule" data-tone="keep"><p><span>Keep</span><b>within <V>{Math.round(rules.keep_band * 100)}%</V></b></p><p>either way: keep it, and change one thing.</p></div>
                <div className="sv-rule" data-tone="stop"><p><span>Stop</span><b>below <V>{ratio(rules.stop_ratio)}</V></b></p><p>for {countWord(n)} posts: stop it, or remake it.</p></div>
              </div>
            </div>
          ) : null}
          <p className="sv-reads-fair">Middles, not averages. The same 90-day window and formula for every account. Under {countWord(n)} posts: too early to read, not a result.</p>
          <SourceLine className="sv-source-plain">Framework 4.5 to 4.7.{rules && !quarterRead ? " The series thresholds are provisional until the first quarter’s data; the quarterly review resets them." : null}</SourceLine>
        </div>
        <Foot ctx={ctx} />
      </div>
    </Slide>
  );
}

/* ── 54 Tests ─────────────────────────────────────────────────────────── */

export function TestsSlide({ ctx }: { ctx: TemplateContext }) {
  const tests = ctx.doc.measuring!.tests;
  const t = slideText(ctx);
  const n = tests.length;
  const per = [...new Set(tests.map((x) => x.posts_per_version))];
  const fallback = `${countWord(n).replace(/^./, (c) => c.toUpperCase())} test${n === 1 ? "" : "s"},${per.length === 1 ? ` *read after ${countWord(per[0])} posts each.*` : " *each read after a fixed number of posts.*"}`;
  return (
    <Slide>
      <div className="sv-col sv-fill">
        <Eyebrow ctx={ctx} />
        <Headline text={t.headline} fallback={fallback} />
        <div className="sv-tests">
          <div className="sv-tests-row">
            {tests.slice(0, 3).map((test, i) => (
              <div key={test.id} className="sv-test">
                <p className="sv-test-key">Test {i + 1}</p>
                <V as="p" className="sv-test-guess">{test.guess}</V>
                <span className="sv-push" />
                <p className="sv-test-measure">
                  <span className="sv-test-pill"><V>{test.measure_name ?? test.measure_id}</V></span>
                  {test.measure_definition ? <V>{test.measure_definition}</V> : null}
                </p>
                <div className="sv-test-split">
                  {test.versions.slice(0, 2).map((v, j) => (
                    <div key={v.name} className="sv-test-version" data-side={j ? "b" : "a"}>
                      <b className="sv-test-letter">{j ? "B" : "A"}</b>
                      <V as="p" className="sv-test-name">{v.name}</V>
                      {v.line ? <V as="p" className="sv-test-desc">{v.line}</V> : null}
                      <span className="sv-push" />
                      <div className="sv-test-posts">{Array.from({ length: test.posts_per_version }, (_, k) => <i key={k} />)}</div>
                      <p className="sv-test-n"><V>{test.posts_per_version}</V> posts</p>
                    </div>
                  ))}
                  <span className="sv-test-vs">vs</span>
                </div>
                <p className="sv-test-starts"><span>Starts</span>{test.starts ? <V>{test.starts}</V> : <span className="sv-test-none">Not started</span>}</p>
              </div>
            ))}
          </div>
          <p className="sv-reads-fair">Each version gets the same number of posts, in the same weeks, on the same platform. The middle of one against the middle of the other, nothing read before the last post.</p>
          <SourceLine className="sv-source-plain">Tests: judged, from chapters {chapterSpec("brand")?.number} and {chapterSpec("content")?.number}; each is a guess with its measure, read after a fixed number of posts per version decided before it starts (framework 4.6).</SourceLine>
        </div>
        <Foot ctx={ctx} />
      </div>
    </Slide>
  );
}
